import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Loader2, ArrowLeft, CheckCircle2, XCircle, BookOpen, Sun, Moon,
    AlertCircle, X, GraduationCap, FileText, Clock, ArrowRight, Info, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    useConferenceOutputDecisionByToken,
    useAgreeJournalDecision,
    useDeclineToProceedingDecision,
} from "@/services/conferenceOutputPublicService";
import { motion, AnimatePresence } from "framer-motion";

interface LaravelErrorResponse {
    message: string;
    errors?: Record<string, string[]>;
}

const parseLaravelError = (error: unknown): string => {
    if (axios.isAxiosError<LaravelErrorResponse>(error)) {
        const data = error.response?.data;
        if (data?.errors) {
            const firstErrorKey = Object.keys(data.errors)[0];
            return data.errors[firstErrorKey][0];
        }
        return data?.message || "Terjadi kesalahan sistem. Silakan coba lagi.";
    }
    if (error instanceof Error) return error.message;
    return "Terjadi kesalahan yang tidak diketahui.";
};

export default function ConferenceOutputDecisionPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [isDark, setIsDark] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: "error" | "success" } | null>(null);
    const [confirmAction, setConfirmAction] = useState<"agree" | "decline" | null>(null);
    const [declineEmailStatus, setDeclineEmailStatus] = useState<boolean | null>(null);

    const showToast = (message: string, type: "error" | "success" = "error") => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 5000);
    };

    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme === "dark") {
            setIsDark(true);
            document.documentElement.classList.add("dark");
        }
    }, []);

    const toggleDarkMode = () => {
        const newMode = !isDark;
        setIsDark(newMode);
        document.documentElement.classList.toggle("dark", newMode);
        localStorage.setItem("theme", newMode ? "dark" : "light");
    };

    const { data: decision, isLoading, isError } = useConferenceOutputDecisionByToken(token);
    const agreeMutation = useAgreeJournalDecision(token);
    const declineMutation = useDeclineToProceedingDecision(token);

    const handleAgree = () => {
        agreeMutation.mutate(undefined, {
            onSuccess: () => {
                setConfirmAction(null);
                showToast("Pilihan Anda untuk melanjutkan ke jurnal berhasil dicatat.", "success");
            },
            onError: (error) => {
                setConfirmAction(null);
                showToast(parseLaravelError(error), "error");
            },
        });
    };

    const handleDecline = () => {
        declineMutation.mutate(undefined, {
            onSuccess: (data) => {
                setConfirmAction(null);
                setDeclineEmailStatus(data?.email_sent ?? null);
                showToast("Pilihan Anda untuk melanjutkan ke proceeding berhasil dicatat.", "success");
            },
            onError: (error) => {
                setConfirmAction(null);
                showToast(parseLaravelError(error), "error");
            },
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                <p className="text-slate-500 dark:text-slate-400 font-medium tracking-wide">Memuat informasi keputusan...</p>
            </div>
        );
    }

    if (isError || !decision) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 text-center transition-colors">
                <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mb-6">
                    <XCircle className="w-10 h-10" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Link Tidak Valid</h1>
                <p className="text-slate-500 dark:text-slate-400 max-w-md mb-8">Link ini tidak valid, sudah kedaluwarsa, atau sudah pernah digunakan sebelumnya.</p>
                <Button onClick={() => navigate("/")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 h-12 font-bold">Kembali ke Beranda</Button>
            </div>
        );
    }

    const journal = decision.scopus_journal_id ? decision.scopus_journal : decision.sinta_journal;
    const journalTypeLabel = decision.scopus_journal_id ? "Scopus" : "Sinta";
    const isFinal = decision.author_decision !== "pending";

    const formatDate = (isoString: string | null) => {
        if (!isoString) return "-";
        // Selalu format dengan timezone WITA (Asia/Makassar, timezone server) supaya tanggal deadline
        // yang tampil ke penulis konsisten, tidak tergantung timezone browser masing-masing penulis.
        return new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Makassar" }).format(new Date(isoString));
    };

    return (
        <div className="min-h-screen bg-[#FAF9F6] dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 py-8 px-4 transition-colors relative">
            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: -50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.9 }}
                        className="fixed top-24 left-1/2 -translate-x-1/2 z-50 px-4 w-full max-w-md"
                    >
                        <div className={`flex items-start gap-3 p-4 rounded-xl shadow-2xl border ${toast.type === "error" ? "bg-white border-red-200 text-red-700 dark:bg-slate-900 dark:border-red-900/50 dark:text-red-400" : "bg-white border-green-200 text-green-700 dark:bg-slate-900 dark:border-green-900/50 dark:text-green-400"}`}>
                            {toast.type === "error" ? <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" /> : <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />}
                            <p className="text-sm font-medium leading-snug flex-1">{toast.message}</p>
                            <button type="button" onClick={() => setToast(null)} className="opacity-50 hover:opacity-100 transition-opacity">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="max-w-3xl mx-auto space-y-6">
                <div className="flex items-center justify-between mb-6">
                    <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Kembali ke Beranda
                    </button>
                    <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="rounded-full w-10 h-10 bg-slate-200/50 hover:bg-slate-300/50 dark:bg-slate-800 dark:hover:bg-slate-700 shrink-0 text-slate-700 dark:text-slate-200 transition-colors">
                        {isDark ? <Sun className="h-4 w-4 text-yellow-400" /> : <Moon className="h-4 w-4" />}
                    </Button>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <div className={`p-6 md:p-8 text-white relative overflow-hidden flex items-center gap-4 ${isFinal ? (decision.author_decision === "agreed" ? "bg-emerald-600 dark:bg-emerald-700" : "bg-blue-600 dark:bg-blue-900") : "bg-blue-600 dark:bg-blue-900"}`}>
                        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
                        <div className="relative z-10 bg-white/20 p-3.5 rounded-xl backdrop-blur-sm border border-white/20 shrink-0">
                            <GraduationCap className="w-7 h-7 text-white" strokeWidth={2.5} />
                        </div>
                        <div className="relative z-10">
                            <p className="text-white/80 text-[10px] font-bold uppercase tracking-widest mb-0.5">KONASPI XII - Output Conference</p>
                            <h1 className="text-lg sm:text-xl font-black tracking-tight">Pilihan Publikasi Paper Anda</h1>
                        </div>
                    </div>

                    <div className="p-6 md:p-8 space-y-6">
                        <div className="pb-5 border-b border-slate-100 dark:border-slate-800">
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5" /> Judul Paper
                            </span>
                            <p className="font-bold text-base text-slate-900 dark:text-white leading-relaxed">{decision.registration?.submission_title || "-"}</p>
                            {decision.registration?.submission_authors && (
                                <p className="text-xs text-slate-500 mt-1.5">
                                    <span className="font-medium text-slate-400">Penulis:</span> {decision.registration.submission_authors}
                                </p>
                            )}
                            {decision.conference?.name && (
                                <p className="text-xs text-slate-500 mt-1">
                                    <span className="font-medium text-slate-400">Track:</span> {decision.conference.name}
                                </p>
                            )}
                        </div>

                        {journal && (
                            <div className="pb-5 border-b border-slate-100 dark:border-slate-800">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                                    <BookOpen className="w-3.5 h-3.5" /> Rekomendasi Jurnal ({journalTypeLabel})
                                </span>
                                <div className="bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-2xl p-4">
                                    <p className="font-bold text-sm text-slate-900 dark:text-white">{journal.journal_name}</p>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {journal.quartile && (
                                            <span className="text-[11px] font-bold uppercase px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded-md">{journal.quartile}</span>
                                        )}
                                        {journal.sinta_rank && (
                                            <span className="text-[11px] font-bold uppercase px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded-md">{journal.sinta_rank}</span>
                                        )}
                                    </div>
                                    {journal.editor_in_chief && (
                                        <p className="text-xs text-slate-500 mt-2">Editor in Chief: {journal.editor_in_chief}</p>
                                    )}
                                    {journal.journal_url && (
                                        <a href={journal.journal_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline mt-3">
                                            <ExternalLink className="w-3.5 h-3.5" /> Kunjungi Website Jurnal
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {decision.decision_deadline && decision.author_decision === "pending" && (
                            <div className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/30 rounded-2xl p-4 flex items-start gap-3">
                                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                <p className="text-sm text-amber-900 dark:text-amber-200/90">
                                    Mohon berikan keputusan sebelum <strong>{formatDate(decision.decision_deadline)}</strong>.
                                </p>
                            </div>
                        )}

                        {decision.author_decision === "pending" ? (
                            <div className="space-y-4">
                                <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 flex items-start gap-3">
                                    <Info className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
                                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                        Silakan pilih salah satu: melanjutkan naskah Anda ke proses review jurnal terindeks di atas, atau menerbitkannya di prosiding konferensi terindeks.
                                    </p>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <Button
                                        onClick={() => setConfirmAction("agree")}
                                        disabled={agreeMutation.isPending || declineMutation.isPending}
                                        className="h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg shadow-emerald-600/20"
                                    >
                                        <CheckCircle2 className="w-5 h-5 mr-2" /> Lanjut ke Jurnal
                                    </Button>
                                    <Button
                                        onClick={() => setConfirmAction("decline")}
                                        disabled={agreeMutation.isPending || declineMutation.isPending}
                                        variant="outline"
                                        className="h-14 rounded-2xl border-slate-300 dark:border-slate-700 font-bold"
                                    >
                                        Pilih Proceeding <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-slate-50/30 dark:bg-slate-900/50 rounded-2xl p-6 text-center flex flex-col items-center">
                                {decision.author_decision === "agreed" ? (
                                    <>
                                        <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 rounded-full flex items-center justify-center mb-4">
                                            <CheckCircle2 className="w-8 h-8" />
                                        </div>
                                        <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Anda Memilih Jurnal</h2>
                                        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
                                            Tim panitia akan segera memproses submission Anda ke jurnal yang direkomendasikan. Pantau email Anda untuk informasi lebih lanjut.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 text-blue-500 rounded-full flex items-center justify-center mb-4">
                                            <ArrowRight className="w-8 h-8" />
                                        </div>
                                        <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Anda Memilih Proceeding</h2>
                                        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
                                            {declineEmailStatus === false
                                                ? "Keputusan Anda sudah kami catat, namun email link registrasi gagal terkirim otomatis. Silakan hubungi panitia."
                                                : "Silakan cek email Anda untuk link registrasi proceeding lengkap dengan formulir metadata, unggah camera-ready, dan copyright form."}
                                        </p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="text-center text-xs text-slate-400 dark:text-slate-500 pb-10">
                    <p>Jika ada pertanyaan, silakan hubungi panitia KONASPI XII.</p>
                </div>
            </div>

            <AnimatePresence>
                {confirmAction && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setConfirmAction(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-800"
                        >
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                                {confirmAction === "agree" ? "Konfirmasi Pilihan Jurnal" : "Konfirmasi Pilihan Proceeding"}
                            </h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">
                                {confirmAction === "agree"
                                    ? "Anda yakin ingin melanjutkan naskah ini ke proses review jurnal? Keputusan ini bersifat final dan tidak dapat diubah kembali."
                                    : "Anda yakin ingin menerbitkan naskah ini di prosiding konferensi (bukan jurnal)? Keputusan ini bersifat final dan tidak dapat diubah kembali."}
                            </p>
                            <div className="flex gap-3">
                                <Button variant="outline" onClick={() => setConfirmAction(null)} className="flex-1 rounded-xl h-11">Batal</Button>
                                <Button
                                    onClick={confirmAction === "agree" ? handleAgree : handleDecline}
                                    disabled={agreeMutation.isPending || declineMutation.isPending}
                                    className={`flex-1 rounded-xl h-11 text-white font-bold ${confirmAction === "agree" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}`}
                                >
                                    {(agreeMutation.isPending || declineMutation.isPending) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                    Ya, Lanjutkan
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
